import React from 'react';
import ProfileCard from '@/components/ProfileCard';
import LinkButtonStack from '@/components/LinkButtonStack';
import IntakeFormCard from '@/components/IntakeFormCard';
import EmailCaptureForm from '@/components/EmailCaptureForm';
import useIngressOrigin from '@/hooks/useIngressOrigin';
import { BIO } from '@/constants/testIds';

// Hard-coded production content for the live demo. Centralized here so the
// component tree stays clean and Shopify-embed friendly (one source of truth).
const PROFILE = {
  name: 'Brandon Williamson',
  title: 'Chief Architect & Designer // PRSVR Fashion House',
  bio:
    'Engineering independent retail ecosystems, autonomous commerce infrastructure, and high-weight design systems for outliers.',
  photoUrl: '/profile-brandon.jpg',
};

const DILA_URL = 'https://d-i-l-a-design-house.myshopify.com/';

// Hit Up Coach B sub-links — L'CHAIM App pinned at the top ("App" alphabetizes
// ahead of Instagram), then alphabetical by platform, then by handle.
const COACH_SUBLINKS = [
  {
    platform: 'lchaim',
    platformLabel: 'App',
    handle: "The L'CHAIM App (The 13th Year)",
    url: 'https://prsvr.store/academy/lchaim',
    testId: BIO.coachSubLchaim,
  },
  {
    platform: 'instagram',
    platformLabel: 'Instagram',
    handle: '@hitupcoachb',
    url: 'https://instagram.com/hitupcoachb',
    testId: BIO.coachSubInstagramHitup,
  },
  {
    platform: 'instagram',
    platformLabel: 'Instagram',
    handle: '@prsvr',
    url: 'https://instagram.com/prsvr',
    testId: BIO.coachSubInstagramPrsvr,
  },
  {
    platform: 'linkedin',
    platformLabel: 'LinkedIn',
    handle: 'in/prsvr',
    url: 'https://www.linkedin.com/in/prsvr/',
    testId: BIO.coachSubLinkedin,
  },
  {
    platform: 'patreon',
    platformLabel: 'Patreon',
    handle: 'BrandonWilliamsonLLC',
    url: 'https://patreon.com/BrandonWilliamsonLLC',
    testId: BIO.coachSubPatreon,
  },
  {
    platform: 'youtube',
    platformLabel: 'YouTube',
    handle: '@BrandonWilliamsonLLC',
    url: 'https://www.youtube.com/@BrandonWilliamsonLLC',
    testId: BIO.coachSubYoutube,
  },
];

const LINKS = [
  {
    type: 'link',
    label: 'PRSVR',
    url: 'https://prsvr.store',
    testId: BIO.link1,
  },
  {
    type: 'link',
    label: 'DILA Design House',
    url: DILA_URL,
    testId: BIO.link2,
  },
  {
    type: 'link',
    label: 'Scholar Athlete Academy',
    url: 'https://prsvr.store/academy',
    testId: BIO.link3,
  },
  {
    type: 'group',
    label: 'Hit Up Coach B',
    toggleTestId: BIO.coachToggle,
    panelTestId: BIO.coachPanel,
    subLinks: COACH_SUBLINKS,
  },
];

// Map a detected ingress origin to the link block that should be highlighted.
// Fallback safety: any unmapped or null origin yields no highlight, which
// produces a balanced static presentation across all corporate pillars.
const originToHighlightedTestId = (origin) => {
  if (origin === 'academy') return BIO.link3;
  if (origin === 'dila' || origin === 'instagram') return BIO.link2;
  return null;
};

const BioPage = () => {
  const origin = useIngressOrigin();
  const highlightedTestId = originToHighlightedTestId(origin);

  return (
    <main
      className="lib-page"
      data-testid={BIO.page}
      data-ingress-origin={origin || 'none'}
    >
      <div className="lib-container">
        <ProfileCard
          name={PROFILE.name}
          title={PROFILE.title}
          bio={PROFILE.bio}
          photoUrl={PROFILE.photoUrl}
        />
        <EmailCaptureForm />
        <LinkButtonStack
          links={LINKS}
          highlightedTestId={highlightedTestId}
        />
        <IntakeFormCard />
        <footer className="lib-footer">
          <span>
            Designed by{' '}
            <a
              className="lib-footer-link"
              href={DILA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={BIO.footerDilaLink}
            >
              D.I.L.A.
            </a>
          </span>
        </footer>
      </div>
    </main>
  );
};

export default BioPage;
